import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import config from '../config';
import { notifyRbacUpdated } from './rbac';
import { getBrand } from './brand';
import { subscriptionApi, getCompanyId } from './components/subscriptionApi';
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Hotel as HotelIcon,
  Home as HomeSharp,
  MeetingRoom as MeetingRoomIcon,
  AddBox as AddBoxIcon,
  Inventory as InventoryIcon,
  ChevronLeft as ChevronLeftIcon,
  ExitToApp as ExitToAppIcon,
  ExpandLess,
  ExpandMore,
  Work as WorkIcon,
  Settings as SettingsIcon,
  Description as DescriptionIcon,
  MonetizationOn as MonetizationOnIcon,
  KeyboardArrowRight as ArrowIcon,
  Lock as LockIcon,
  ArrowCircleUp as UpgradeIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const drawerWidthExpanded = 256;
const drawerWidthCollapsed = 68;

const company = JSON.parse(sessionStorage.getItem('companyDetails'));
const Users = JSON.parse(sessionStorage.getItem('user'));
const userName = Users?.name || 'Admin';
// Restaurant-only business → brand as "Restaurant Desk" everywhere.
const isRestaurantOnly = sessionStorage.getItem('propertType') === 'R';
// #2: wizard-driven module visibility. A restaurant-only property has NO hotel;
// a hotel shows restaurant modules only if it actually configured a restaurant.
const restaurantEnabled = isRestaurantOnly || company?.hasRestaurant === true;
const hotelEnabled = !isRestaurantOnly;
// Whole top-level groups that belong to one domain — hidden when that domain
// isn't enabled, so no Restaurant/Hotel module (or name) shows anywhere.
const RESTAURANT_MENUS = new Set([
  'Resturant Dashbord',
  'Table Reservations',
  'Restaurant Order',
  'Manage Restaurant',
]);
const HOTEL_MENUS = new Set([
  'Quick Booking',
  'Booking',
  'Hotel Master',
  'Other Facility',
  'Housekeeping',
]);
const userRole =
  Users?.role || (isRestaurantOnly ? 'Restaurant Staff' : 'Hotel Staff');
// Domain-aware brand: restaurant host / restaurant-only property → "DineMaster",
// otherwise "Hotel Desk". Shown everywhere the brand name/subtitle appears.
const APP_BRAND = getBrand();
const brandName = APP_BRAND.name;
const brandSub = APP_BRAND.sub;
const userInitials = userName
  .split(' ')
  .map((n) => n[0])
  .join('')
  .toUpperCase()
  .slice(0, 2);

/* ── Sidebar palette ── */
const S = {
  bg: '#0F172A',
  bgHover: 'rgba(255,255,255,0.055)',
  bgActive: 'rgba(96,165,250,0.14)',
  border: 'rgba(255,255,255,0.07)',
  textDim: 'rgba(148,163,184,0.8)',
  textHover: 'rgba(255,255,255,0.92)',
  textActive: '#93c5fd',
  accent: '#1A56DB',
  accentL: '#60a5fa',
};

// Icon registry for DB-driven menus — MenuMaster.Icon stores an icon NAME string,
// mapped here to a component. Unknown/empty names fall back to a generic icon.
const MENU_ICONS = {
  Dashboard: <DashboardIcon />,
  Hotel: <HotelIcon />,
  Home: <HomeSharp />,
  MeetingRoom: <MeetingRoomIcon />,
  AddBox: <AddBoxIcon />,
  Inventory: <InventoryIcon />,
  Work: <WorkIcon />,
  Settings: <SettingsIcon />,
  Description: <DescriptionIcon />,
  MonetizationOn: <MonetizationOnIcon />,
};
const iconFor = (name) => MENU_ICONS[name] || <DashboardIcon />;

const fullMenuStructure = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/admin/dashboard',
    menuKey: 'dashboard',
  },
  {
    text: 'Multi-Hotel Dashboard',
    icon: <DashboardIcon />,
    path: '/admin/MultiHotelDashboard',
    menuKey: 'dashboard',
  },
  {
    // SUPER ADMIN ONLY — gated on Users.isMaster (see the menu filter), so a
    // normal company user never sees this regardless of menuKey grants.
    text: 'Company Management',
    icon: <DashboardIcon />,
    path: '/admin/SuperAdminCompanies',
    menuKey: 'SuperAdminCompanies',
    masterOnly: true,
  },
  {
    // SUPER ADMIN ONLY — DB-driven menu management.
    text: 'Menu Management',
    icon: <SettingsIcon />,
    path: '/admin/MenuMaster',
    menuKey: 'MenuMaster',
    masterOnly: true,
  },
  {
    text: 'Quick Booking',
    icon: <DashboardIcon />,
    path: '/admin/QuickBooking',
    menuKey: 'QuickBooking',
  },
  {
    text: 'Resturant Dashbord',
    icon: <DashboardIcon />,
    path: '/admin/resturantdashbord',
    menuKey: 'resturantdashbord',
  },
  {
    text: 'Table Reservations',
    icon: <DashboardIcon />,
    path: '/admin/Reservations',
    menuKey: 'Reservations',
  },
  {
    text: 'Booking',
    icon: <SettingsIcon />,
    menuKey: null,
    children: [
      {
        text: 'Room Booking',
        icon: <InventoryIcon />,
        path: '/admin/manageinventory',
        menuKey: 'manageinventory',
      },
      {
        text: 'Hourly Booking',
        icon: <InventoryIcon />,
        path: '/admin/HourlyBooking',
        menuKey: 'HourlyBooking',
      },
      {
        text: 'Manage Bookings',
        icon: <DescriptionIcon />,
        path: '/admin/BookingReport',
        menuKey: 'BookingReport',
      },

      {
        text: 'Hold Room',
        icon: <DescriptionIcon />,
        path: '/admin/HoldRoom',
        menuKey: 'HoldRoom',
      },
      {
        text: 'Update Booking Rooms',
        icon: <DescriptionIcon />,
        path: '/admin/Changeroom',
        menuKey: 'Changeroom',
      },
      {
        text: 'Calender View',
        icon: <DescriptionIcon />,
        path: '/admin/Calender',
        menuKey: 'Calender',
      },
    ],
  },
  {
    text: 'Hotel Master',
    icon: <HotelIcon />,
    menuKey: 'hotels',
    children: [
      // {
      //   text: 'Add Hotel',
      //   icon: <AddBoxIcon />,
      //   path: '/admin/hotels/add',
      //   menuKey: 'hotels/add',
      // },
      {
        text: 'Add Room',
        icon: <AddBoxIcon />,
        path: '/admin/rooms/add',
        menuKey: 'rooms/add',
      },
      {
        text: 'Room Category Master',
        icon: <MeetingRoomIcon />,
        path: '/admin/roomtype',
        menuKey: 'roomtype',
      },
      {
        text: 'Manage Package',
        icon: <WorkIcon />,
        path: '/admin/managepackage',
        menuKey: 'managepackage',
      },
      {
        text: 'Add-On Master',
        icon: <DescriptionIcon />,
        path: '/admin/Addonm',
        menuKey: 'Addonm',
      },

      {
        text: 'Manage Hotel',
        icon: <HotelIcon />,
        path: '/admin/hotels',
        menuKey: 'hotels',
      },
    ],
  },
  {
    text: 'Manage User',
    icon: <HomeSharp />,
    menuKey: 'ManageUser',
    children: [
      {
        text: 'User Create',
        icon: <DescriptionIcon />,
        path: '/admin/CreateUser',
        menuKey: 'CreateUser',
      },
      {
        text: 'Menu Permission',
        icon: <DescriptionIcon />,
        path: '/admin/UserPermission',
        menuKey: 'UserPermission',
      },
      {
        text: 'User Report',
        icon: <DescriptionIcon />,
        path: '/admin/UserReport',
        menuKey: 'UserReport',
      },
      {
        text: 'Roles & Permissions',
        icon: <DescriptionIcon />,
        path: '/admin/RbacRoles',
        menuKey: 'UserPermission',
      },
      {
        text: 'User Permission Override',
        icon: <DescriptionIcon />,
        path: '/admin/RbacUserOverrides',
        menuKey: 'UserPermission',
      },
      {
        text: 'Cutomer Wallet Report',
        icon: <DescriptionIcon />,
        path: '/admin/Customerwallet',
        menuKey: 'Customerwallet',
      },
    ],
  },
  {
    text: 'Manage Agent / Coupon',
    icon: <HomeSharp />,
    menuKey: 'ManageAgent',
    children: [
      {
        text: 'Agent Master',
        icon: <DescriptionIcon />,
        path: '/admin/Agent',
        menuKey: 'Agent',
      },
      {
        text: 'Coupon Master',
        icon: <DescriptionIcon />,
        path: '/admin/Couponmaster',
        menuKey: 'Couponmaster',
      },
    ],
  },
  {
    text: 'Restaurant Order',
    icon: <HomeSharp />,
    menuKey: 'RestaurantOrder',
    children: [
      {
        text: 'Create Order',
        icon: <AddBoxIcon />,
        path: '/admin/CreateOrder',
        menuKey: 'CreateOrder',
      },
      {
        text: 'Quick Bill',
        icon: <AddBoxIcon />,
        path: '/admin/QuickBill',
        menuKey: 'CreateOrder',
      },
      {
        text: 'View Room Orders',
        icon: <AddBoxIcon />,
        path: '/admin/Order',
        menuKey: 'Order',
      },
      {
        text: 'View Table Orders',
        icon: <AddBoxIcon />,
        path: '/admin/TableOrders',
        menuKey: 'RestaurantOrder',
      },
      {
        text: 'Kitchen Pending Orders',
        icon: <AddBoxIcon />,
        path: '/admin/KitchenPendingOrders',
        menuKey: 'KitchenPendingOrders',
      },
      {
        text: 'KOT Report',
        icon: <AddBoxIcon />,
        path: '/admin/KotReport',
        menuKey: 'KotReport',
      },
      {
        text: 'Restaurant Payments',
        icon: <AddBoxIcon />,
        path: '/admin/RestaurantPayments',
        menuKey: 'RestaurantOrder',
      },
    ],
  },
  {
    text: 'Manage Restaurant',
    icon: <HomeSharp />,
    menuKey: 'restaurant',
    children: [
      {
        text: 'QR Code Generator',
        icon: <AddBoxIcon />,
        path: '/admin/QrCodeGenerator',
        menuKey: 'CreateOrder',
      },
      {
        text: 'Add Department',
        icon: <AddBoxIcon />,
        path: '/admin/AddDepartment',
        menuKey: 'AllCategory',
      },
      {
        text: 'Add Area',
        icon: <AddBoxIcon />,
        path: '/admin/Createarea',
        menuKey: 'Createarea',
      },
      {
        text: 'Add Item Category',
        icon: <AddBoxIcon />,
        path: '/admin/Category',
        menuKey: 'Category',
      },
      {
        text: 'Add Item',
        icon: <AddBoxIcon />,
        path: '/admin/AddItem',
        menuKey: 'AddItem',
      },
      {
        text: 'Menu Upload History',
        icon: <AddBoxIcon />,
        path: '/admin/MenuUploadHistory',
        menuKey: 'AddItem',
      },
      {
        text: 'Area Pricing',
        icon: <AddBoxIcon />,
        path: '/admin/AreaPricing',
        menuKey: 'AreaPricing',
      },
      {
        text: 'Variant Management',
        icon: <AddBoxIcon />,
        path: '/admin/VariantManagement',
        menuKey: 'VariantManagement',
      },
      {
        text: 'Store Master',
        icon: <AddBoxIcon />,
        path: '/admin/StoreMaster',
        menuKey: 'StoreMaster',
      },
      {
        text: 'Item StoreMapping',
        icon: <AddBoxIcon />,
        path: '/admin/ItemStoreMapping',
        menuKey: 'ItemStoreMapping',
      },

      {
        text: 'Stock TransactionHistory',
        icon: <AddBoxIcon />,
        path: '/admin/StockTransactionHistory',
        menuKey: 'StockTransactionHistory',
      },

      {
        text: 'Item Report',
        icon: <AddBoxIcon />,
        path: '/admin/ItemReport',
        menuKey: 'ItemReport',
      },
      {
        text: 'Restaurant Invoice Report',
        icon: <AddBoxIcon />,
        path: '/admin/RestaurantInvoiceReport',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        text: 'Restaurant Activity',
        icon: <AddBoxIcon />,
        path: '/admin/RestaurantActivity',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        text: 'Order History',
        icon: <AddBoxIcon />,
        path: '/admin/OrderHistory',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        text: 'Booking History',
        icon: <AddBoxIcon />,
        path: '/admin/BookingHistory',
        menuKey: 'RestaurantInvoiceReport',
      },
      {
        text: 'Restaurant Guests',
        icon: <AddBoxIcon />,
        path: '/admin/RestaurantGuestReport',
        menuKey: 'RestaurantGuestReport',
      },
      {
        text: 'All Category Report',
        icon: <AddBoxIcon />,
        path: '/admin/AllCategory',
        menuKey: 'AllCategory',
      },
    ],
  },
  {
    text: 'Other Facility',
    icon: <MeetingRoomIcon />,
    menuKey: 'OtherFacility',
    children: [
      // {
      //   text: 'Banquet Hall',
      //   icon: <AddBoxIcon />,
      //   path: '/admin/banquethall/add',
      //   menuKey: 'banquethall/add',
      // },
      // {
      //   text: 'Bulk Booking Wizard',
      //   icon: <AddBoxIcon />,
      //   path: '/admin/RoomBookingWizard',
      //   menuKey: 'RoomBookingWizard',
      // },
      {
        text: 'Bulk Bookings',
        icon: <AddBoxIcon />,
        path: '/admin/BulkBookings',
        menuKey: 'BulkBookings',
      },
    ],
  },
  {
    text: 'Reports',
    icon: <MeetingRoomIcon />,
    menuKey: null,
    children: [
      {
        text: 'Booking Report',
        icon: <DescriptionIcon />,
        path: '/admin/Bookingfilter',
        menuKey: 'Bookingfilter',
      },
      {
        text: 'Hotel List',
        icon: <HotelIcon />,
        path: '/admin/hotels',
        menuKey: 'hotels',
      },
      {
        text: 'Room List',
        icon: <MeetingRoomIcon />,
        path: '/admin/rooms',
        menuKey: 'rooms',
      },
      {
        text: 'Package Report',
        icon: <DescriptionIcon />,
        path: '/admin/packagereport',
        menuKey: 'packagereport',
      },
      {
        text: 'Add-On Amount',
        icon: <DescriptionIcon />,
        path: '/admin/AddonProfitReport',
        menuKey: 'AddonProfitReport',
      },
      {
        text: 'Invoice Report',
        icon: <DescriptionIcon />,
        path: '/admin/InvoiceReport',
        menuKey: 'InvoiceReport',
      },
      {
        text: 'Guest Report',
        icon: <DescriptionIcon />,
        path: '/admin/Guestreport',
        menuKey: 'Guestreport',
      },
      {
        text: 'Agent Report',
        icon: <DescriptionIcon />,
        path: '/admin/AgentReport',
        menuKey: 'AgentReport',
      },

      {
        text: 'Agent Commission Report',
        icon: <DescriptionIcon />,
        path: '/admin/Agentcommissionreport',
        menuKey: 'Agentcommissionreport',
      },
      {
        text: 'Hold / Maintenance Rooms',
        icon: <DescriptionIcon />,
        path: '/admin/Holdreport',
        menuKey: 'Holdreport',
      },
      {
        text: 'Night Audit',
        icon: <DescriptionIcon />,
        path: '/admin/NightAuditReport',
        menuKey: 'NightAuditReport',
      },
      {
        text: 'GST Reports',
        icon: <DescriptionIcon />,
        path: '/admin/GstReports',
        menuKey: 'GstReports',
      },
      {
        text: 'Activity Log',
        icon: <DescriptionIcon />,
        path: '/admin/ActivityLog',
        menuKey: 'ActivityLog',
      },
      {

        text: 'Subscription Payments',
        icon: <MonetizationOnIcon />,
        path: '/admin/SubscriptionPaymentReport',
        // Dedicated permission key for this report. alwaysShow keeps it visible
        // under Reports without needing the permission granted first.
        menuKey: 'SubscriptionPaymentReport',
        alwaysShow: true,
      },
      {
        text: 'Edit Booking / Order',
        icon: <DescriptionIcon />,
        path: '/admin/AmountCorrection',
        menuKey: 'ActivityLog',
      }
    ],
  },
  {
    text: 'Purchase',
    icon: <MeetingRoomIcon />,
    menuKey: 'mainpurchase',
    children: [
      {
        text: 'Stock Dashboard',
        icon: <AddBoxIcon />,
        path: '/admin/StockDashboard',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Stock Transfer',
        icon: <AddBoxIcon />,
        path: '/admin/StockTransfer',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Opening / Closing Report',
        icon: <AddBoxIcon />,
        path: '/admin/StockOpeningClosingReport',
        menuKey: 'StockDashboard',
      },
      {
        text: 'Supplier Master',
        icon: <AddBoxIcon />,
        path: '/admin/SupplierMaster',
        menuKey: 'SupplierMaster',
      },
      {
        text: 'Store Purchase',
        icon: <AddBoxIcon />,
        path: '/admin/StorePurchase',
        menuKey: 'StorePurchase',
      },
      {
        text: 'Recipe Master',
        icon: <AddBoxIcon />,
        path: '/admin/RecipeMaster',
        menuKey: 'RecipeMaster',
      },
      {
        text: 'Purchase Reports',
        icon: <AddBoxIcon />,
        path: '/admin/PurchaseReports',
        menuKey: 'PurchaseReports',
      },
      {
        text: 'Indent / Order',
        icon: <AddBoxIcon />,
        path: '/admin/IndentEntry',
        menuKey: 'StorePurchase',
      },
      {
        text: 'Indent Orders List',
        icon: <AddBoxIcon />,
        path: '/admin/IndentList',
        menuKey: 'StorePurchase',
      },
    ],
  },
  {
    text: 'Payment',
    icon: <AddBoxIcon />,
    path: '/admin/Paymentreport',
    menuKey: 'Paymentreport',
  },
  {
    text: 'Petty Cash Management',
    icon: <MonetizationOnIcon />,
    path: '/admin/pettycashmanagement',
    menuKey: 'PettyCashManagement',
  },

  /* ===== Spa POS ===== */
  {
    text: 'Spa POS',
    icon: <WorkIcon />,
    menuKey: null,
    children: [
      {
        text: 'Service Master',
        icon: <DescriptionIcon />,
        path: '/admin/Spa/Services',
        menuKey: 'SpaServices',
      },
      {
        text: 'Therapist Master',
        icon: <DescriptionIcon />,
        path: '/admin/Spa/Therapists',
        menuKey: 'SpaTherapists',
      },
      {
        text: 'Spa Bookings',
        icon: <DescriptionIcon />,
        path: '/admin/Spa/Bookings',
        menuKey: 'SpaBookings',
      },
    ],
  },

  /* ===== Laundry POS ===== */
  {
    text: 'Laundry POS',
    icon: <InventoryIcon />,
    menuKey: null,
    children: [
      {
        text: 'Item Master',
        icon: <DescriptionIcon />,
        path: '/admin/Laundry/Items',
        menuKey: 'LaundryItems',
      },
      {
        text: 'Orders',
        icon: <DescriptionIcon />,
        path: '/admin/Laundry/Orders',
        menuKey: 'LaundryOrders',
      },
    ],
  },

  /* ===== Mini-Bar POS ===== */
  {
    text: 'Mini-Bar POS',
    icon: <InventoryIcon />,
    menuKey: null,
    children: [
      {
        text: 'Item Master',
        icon: <DescriptionIcon />,
        path: '/admin/MiniBar/Items',
        menuKey: 'MiniBarItems',
      },
      {
        text: 'Consumption',
        icon: <DescriptionIcon />,
        path: '/admin/MiniBar/Consumption',
        menuKey: 'MiniBarConsumption',
      },
    ],
  },

  /* ===== HR / Payroll ===== */
  {
    text: 'HR / Payroll',
    icon: <WorkIcon />,
    menuKey: null,
    children: [
      {
        text: 'HR Dashboard',
        icon: <DashboardIcon />,
        path: '/admin/Hr/Dashboard',
        menuKey: 'HrDashboard',
      },
      {
        text: 'Reports',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Reports',
        menuKey: 'HrReports',
      },
      {
        text: 'Departments',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Departments',
        menuKey: 'HrDepartments',
      },
      {
        text: 'Designations',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Designations',
        menuKey: 'HrDesignations',
      },
      {
        text: 'Employees',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Employees',
        menuKey: 'HrEmployees',
      },
      {
        text: 'Shifts',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Shifts',
        menuKey: 'HrShifts',
      },
      {
        text: 'Attendance',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Attendance',
        menuKey: 'HrAttendance',
      },
      {
        text: 'Leave Types',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/LeaveTypes',
        menuKey: 'HrLeaveTypes',
      },
      {
        text: 'Leave Requests',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/LeaveRequests',
        menuKey: 'HrLeaveRequests',
      },
      {
        text: 'Overtime',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/Overtime',
        menuKey: 'HrOvertime',
      },
      {
        text: 'Advance Salary',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/AdvanceSalary',
        menuKey: 'HrAdvanceSalary',
      },
      {
        text: 'Loans',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Loans',
        menuKey: 'HrLoans',
      },
      {
        text: 'Salary Structure',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/SalaryStructure',
        menuKey: 'HrSalaryStructure',
      },
      {
        text: 'Payroll',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Payroll',
        menuKey: 'HrPayroll',
      },
      {
        text: 'Salary Payments',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/SalaryPayments',
        menuKey: 'HrSalaryPayments',
      },
      {
        text: 'Service Charge',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/ServiceCharge',
        menuKey: 'HrServiceCharge',
      },
      {
        text: 'Tips',
        icon: <MonetizationOnIcon />,
        path: '/admin/Hr/Tips',
        menuKey: 'HrTips',
      },
      {
        text: 'Staff Meals',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/StaffMeals',
        menuKey: 'HrStaffMeals',
      },
      {
        text: 'Uniform Deposit',
        icon: <DescriptionIcon />,
        path: '/admin/Hr/UniformDeposit',
        menuKey: 'HrUniformDeposit',
      },
    ],
  },

  /* ===== Housekeeping ===== */
  {
    text: 'Housekeeping',
    icon: <MeetingRoomIcon />,
    menuKey: null,
    children: [
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/admin/Housekeeping/Dashboard',
        menuKey: 'HousekeepingDashboard',
      },
      {
        text: 'Tasks',
        icon: <DescriptionIcon />,
        path: '/admin/Housekeeping/Tasks',
        menuKey: 'HousekeepingTasks',
      },
      {
        text: 'Staff Master',
        icon: <DescriptionIcon />,
        path: '/admin/Housekeeping/Staff',
        menuKey: 'HousekeepingStaff',
      },
    ],
  },
  // ── Support kept at the very bottom of the menu ──
  {
    text: 'Support / Complaints',
    icon: <DashboardIcon />,
    path: '/admin/SupportTickets',
    menuKey: 'SupportTickets',
  },
  {
    // Master-admin only (IsMaster) — manage all companies' complaints.
    text: 'Support Management',
    icon: <DashboardIcon />,
    path: '/admin/SupportAdmin',
    menuKey: 'SupportAdmin',
    masterOnly: true,
  },
];

/* ── Sidebar nav item (parent/standalone) ── */
function NavItem({ menu, isOpen, isActive, expanded, onClick }) {
  return (
    <Tooltip title={expanded ? '' : menu.text} placement='right' arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          mx: '8px',
          borderRadius: '10px',
          mb: '2px',
          px: expanded ? '12px' : '10px',
          py: '8px',
          justifyContent: expanded ? 'initial' : 'center',
          background: isActive ? S.bgActive : 'transparent',
          borderLeft: isActive
            ? `3px solid ${S.accent}`
            : '3px solid transparent',
          color: isActive ? S.textActive : S.textDim,
          transition: 'all 0.15s ease',
          '&:hover': { background: S.bgHover, color: S.textHover },
        }}>
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: expanded ? '10px' : 'auto',
            justifyContent: 'center',
            color: 'inherit',
            '& svg': { fontSize: 18 },
          }}>
          {menu.icon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={menu.text}
            primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: isActive ? 600 : 500,
              fontFamily: 'Inter,sans-serif',
              letterSpacing: '0.15px',
              noWrap: true,
            }}
          />
        )}
        {expanded && menu.children && (
          <Box
            sx={{
              color: 'inherit',
              opacity: 0.6,
              ml: '4px',
              display: 'flex',
              alignItems: 'center',
            }}>
            {isOpen ? (
              <ExpandLess sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 16 }} />
            )}
          </Box>
        )}
      </ListItemButton>
    </Tooltip>
  );
}

/* ── Sidebar child item ── */
function ChildItem({ child, isActive, expanded, onClick }) {
  return (
    <Tooltip title={expanded ? '' : child.text} placement='right' arrow>
      <ListItemButton
        onClick={onClick}
        sx={{
          mx: '8px',
          borderRadius: '8px',
          mb: '1px',
          pl: expanded ? '36px' : '10px',
          pr: '10px',
          py: '6px',
          justifyContent: expanded ? 'initial' : 'center',
          background: isActive ? S.bgActive : 'transparent',
          color: isActive ? S.textActive : 'rgba(100,116,139,0.75)',
          transition: 'all 0.15s ease',
          '&:hover': { background: S.bgHover, color: S.textHover },
        }}>
        {/* dot indicator */}
        {expanded && (
          <Box
            sx={{
              width: isActive ? 6 : 4,
              height: isActive ? 6 : 4,
              borderRadius: '50%',
              bgcolor: isActive ? S.accentL : 'rgba(100,116,139,0.5)',
              mr: '10px',
              flexShrink: 0,
              transition: 'all 0.15s',
            }}
          />
        )}
        {!expanded && <ArrowIcon sx={{ fontSize: 14, color: 'inherit' }} />}
        {expanded && (
          <ListItemText
            primary={child.text}
            primaryTypographyProps={{
              fontSize: '12.5px',
              fontWeight: isActive ? 600 : 400,
              fontFamily: 'Inter,sans-serif',
              letterSpacing: '0.1px',
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );
}

export default function AdminLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [openMenus, setOpenMenus] = useState({});
  const [filteredMenu, setFilteredMenu] = useState([]);
  const createOrderPinnedExpandedRef = useRef(false);

  // When the subscription is expired / missing, lock the menu behind an
  // overlay (logo + Upgrade + Logout stay usable). Driven by the backend.
  const [subLocked, setSubLocked] = useState(false);
  // Plan-wise menu access: { allMenus, menuKeys }. null = not loaded yet.
  const [planAccess, setPlanAccess] = useState(null);

  useEffect(() => {
    const cid = getCompanyId();
    if (!cid) return;
    subscriptionApi
      .getStatus(cid)
      .then((res) => setSubLocked(res?.data?.canAccess === false))
      .catch(() => setSubLocked(false));
    subscriptionApi
      .getCompanyMenus(cid)
      .then((res) => setPlanAccess(res?.data || { allMenus: true }))
      .catch(() => setPlanAccess({ allMenus: true })); // fail open on error
  }, []);

  // #3: DIRECT-URL ACCESS GUARD. The sidebar already hides menus a user lacks,
  // but the page itself stayed reachable by typing its URL. Mirror the SAME
  // per-user menuKey grant rule here and bounce an unauthorized deep-link back
  // to a safe page. Fail-open by design — master admins, and any time the
  // permission list isn't present, are never blocked — so it can never lock a
  // legitimate user out of the app.
  useEffect(() => {
    if (Users?.isMaster) return;
    const raw = sessionStorage.getItem('permissions');
    if (!raw) return; // permissions not loaded yet -> don't block
    let keys;
    try {
      keys = new Set(JSON.parse(raw).map((p) => p.menuKey));
    } catch {
      return;
    }

    // Flatten the static menu into guardable {path, menuKey} entries.
    const flat = [];
    const walk = (arr) =>
      arr.forEach((m) => {
        if (m.path && m.menuKey)
          flat.push({
            path: m.path,
            menuKey: m.menuKey,
            alwaysShow: !!m.alwaysShow,
            masterOnly: !!m.masterOnly,
          });
        if (m.children) walk(m.children);
      });
    walk(fullMenuStructure);

    const path = location.pathname.replace(/\/+$/, '');
    // Match the most specific menu path the current URL falls under, so detail
    // routes like /admin/manageinventory/123 inherit their parent's grant.
    const match = flat
      .filter((e) => path === e.path || path.startsWith(e.path + '/'))
      .sort((a, b) => b.path.length - a.path.length)[0];

    if (!match || match.alwaysShow) return; // unmapped/always-on -> allow

    const planAllowed =
      !planAccess ||
      planAccess.allMenus ||
      (planAccess.menuKeys || []).includes(match.menuKey);

    if (match.masterOnly || !keys.has(match.menuKey) || !planAllowed) {
      navigate('/admin/welcome', { replace: true });
    }
  }, [location.pathname, planAccess]);

  const hasDashboardPermission = filteredMenu.some(
    (m) => m.menuKey === 'dashboard',
  );

  useEffect(() => {
    // Build the menuKey grant set (empty if the user has no permissions yet) —
    // master-admin items don't depend on it, so they still show for a master
    // even when the permission list is absent.
    let keys = new Set();
    const raw = sessionStorage.getItem('permissions');
    if (raw) {
      try {
        const permissions = JSON.parse(raw);
        keys = new Set(permissions.map((p) => p.menuKey));
      } catch {
        keys = new Set();
      }
    }

    // Plan cap: until plan menus load (null) don't restrict; trial/allMenus = no cap.
    const planAllowed = (k) =>
      !planAccess || planAccess.allMenus || (planAccess.menuKeys || []).includes(k);
    const allowed = (c) =>
      c.alwaysShow || (keys.has(c.menuKey) && planAllowed(c.menuKey));
    // Apply the per-user menuKey + master-only visibility rules to a menu array.
    const filterMenus = (arr) =>
      arr
        .map((menu) => {
          // #2: hide entire Hotel / Restaurant module groups per the wizard
          // selection (restaurant-only hides hotel groups, and vice-versa).
          if (RESTAURANT_MENUS.has(menu.text) && !restaurantEnabled) return null;
          if (HOTEL_MENUS.has(menu.text) && !hotelEnabled) return null;
          // Master-admin-only items (Company Management) bypass the per-user
          // menuKey grants — shown ONLY to a master admin, hidden from all
          // normal company users.
          if (menu.masterOnly) {
            return Users?.isMaster ? menu : null;
          }
          if (menu.children) {
            const visibleChildren = menu.children.filter(allowed);
            const parentAllowed = menu.menuKey
              ? keys.has(menu.menuKey) && planAllowed(menu.menuKey)
              : visibleChildren.length > 0;
            return parentAllowed
              ? { ...menu, children: visibleChildren }
              : null;
          }
          return allowed(menu) ? menu : null;
        })
        .filter(Boolean);

    // 1) Render the proven STATIC menu immediately (zero-regression baseline).
    setFilteredMenu(filterMenus(fullMenuStructure));

    // 2) Then ADDITIVELY merge any DB-defined menus (MenuMaster). Only menuKeys
    //    NOT already in the static menu are appended, so this can never break or
    //    duplicate existing navigation. If the fetch fails, the static menu stays.
    const company = JSON.parse(
      sessionStorage.getItem('companyDetails') || '{}',
    );
    const token = sessionStorage.getItem('token');
    if (!company?.id) return;
    fetch(`${config.API_BASE_URL}/menu/menus/${company.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((db) => {
        if (!Array.isArray(db) || db.length === 0) return;

        // Collect every menuKey already defined in the static structure.
        const staticKeys = new Set();
        const collect = (arr) =>
          arr.forEach((m) => {
            if (m.menuKey) staticKeys.add(m.menuKey);
            if (m.children) collect(m.children);
          });
        collect(fullMenuStructure);

        // Top-level DB menus (no parent) whose key isn't already static.
        const extra = db
          .filter((m) => !m.parentId)
          .filter((m) => m.menuKey && !staticKeys.has(m.menuKey))
          .map((parent) => {
            const node = {
              text: parent.text,
              icon: iconFor(parent.icon),
              path: parent.path,
              menuKey: parent.menuKey,
              masterOnly: parent.masterOnly,
            };
            const kids = db.filter((c) => c.parentId === parent.id);
            if (kids.length) {
              node.children = kids.map((k) => ({
                text: k.text,
                icon: iconFor(k.icon),
                path: k.path,
                menuKey: k.menuKey,
              }));
            }
            return node;
          });

        if (extra.length === 0) return;
        setFilteredMenu(filterMenus([...fullMenuStructure, ...extra]));
      })
      .catch(() => {});
  }, [planAccess]);

  useEffect(() => {
    const onCreateOrder =
      location.pathname === '/admin/CreateOrder' ||
      location.pathname.startsWith('/admin/CreateOrder/');
    if (onCreateOrder) {
      if (!createOrderPinnedExpandedRef.current) {
        setDrawerOpen(false);
      }
      return;
    }
    createOrderPinnedExpandedRef.current = false;
    setDrawerOpen(!isMobile);
  }, [isMobile, location.pathname]);

  useEffect(() => {
    const next = {};
    filteredMenu.forEach((menu) => {
      if (menu.children) {
        next[menu.text] = menu.children.some((c) =>
          location.pathname.startsWith(c.path),
        );
      }
    });
    setOpenMenus(next);
  }, [location.pathname, filteredMenu]);

  const toggleDrawer = () => {
    setDrawerOpen((p) => {
      const next = !p;
      const onCreateOrder =
        location.pathname === '/admin/CreateOrder' ||
        location.pathname.startsWith('/admin/CreateOrder/');
      if (onCreateOrder && next) {
        createOrderPinnedExpandedRef.current = true;
      }
      return next;
    });
  };
  const toggleMenu = (key) => setOpenMenus((p) => ({ ...p, [key]: !p[key] }));
  const isSelected = (path) => location.pathname === path;

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('permissions');
      sessionStorage.removeItem('permissionCodes');
      notifyRbacUpdated();
      navigate('/login');
    }
  };

  const drawerWidth = drawerOpen ? drawerWidthExpanded : drawerWidthCollapsed;

  /* ── Drawer content ── */
  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: S.bg,
      }}>
      {/* ── Logo / Company area ── */}
      <Box
        sx={{
          px: drawerOpen ? '16px' : '8px',
          py: '14px',
          borderBottom: `1px solid ${S.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: 64,
        }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            flexShrink: 0,
            background: 'linear-gradient(135deg,#1A56DB,#3b82f6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(26,86,219,0.4)',
            overflow: 'hidden',
          }}>
          <Box
            component='img'
            src={
              company?.logo?.startsWith('http')
                ? company.logo
                : `${config.API_BASE_URL}${company?.logo}`
            }
            alt='logo'
            onError={(e) => {
              e.target.style.display = 'none';
            }}
            sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </Box>
        {drawerOpen && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '13.5px',
                fontWeight: 700,
                color: '#fff',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                letterSpacing: '0.1px',
              }}>
              {company?.name || brandName}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                color: 'rgba(100,116,139,0.8)',
                mt: '2px',
                fontWeight: 500,
              }}>
              {brandSub}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Navigation ── */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          overflowY: subLocked ? 'hidden' : 'auto',
          overflowX: 'hidden',
          py: '8px',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.1)',
            borderRadius: 2,
          },
        }}>
        {/* Lock overlay — hides + disables the menu when subscription is invalid */}
        {subLocked && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              backdropFilter: 'blur(2px)',
              background: 'rgba(15,23,42,0.80)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
              px: 2,
              textAlign: 'center',
              cursor: 'not-allowed',
            }}>
            <LockIcon sx={{ fontSize: 30, color: '#f59e0b' }} />
            {drawerOpen && (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
                  Subscription Required
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: 'rgba(148,163,184,0.9)',
                    lineHeight: 1.4,
                  }}>
                  Choose or renew a plan to unlock the menu.
                </Typography>
                <Button
                  size='small'
                  variant='contained'
                  startIcon={<UpgradeIcon />}
                  onClick={() => {
                    navigate('/admin/Subscription');
                    if (isMobile) setDrawerOpen(false);
                  }}
                  sx={{
                    mt: 0.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                    '&:hover': {
                      background: 'linear-gradient(135deg,#15803d,#16a34a)',
                    },
                  }}>
                  Upgrade Now
                </Button>
              </>
            )}
          </Box>
        )}
        <List
          disablePadding
          sx={{
            pointerEvents: subLocked ? 'none' : 'auto',
            opacity: subLocked ? 0.4 : 1,
          }}>
          {filteredMenu.map((menu) => {
            if (!menu.children) {
              return (
                <NavItem
                  key={menu.text}
                  menu={menu}
                  isActive={isSelected(menu.path)}
                  expanded={drawerOpen}
                  onClick={() => {
                    navigate(menu.path);
                    if (isMobile) setDrawerOpen(false);
                  }}
                />
              );
            }

            const isOpen = openMenus[menu.text] || false;
            const anyChildActive = menu.children.some((c) =>
              location.pathname.startsWith(c.path),
            );

            return (
              <React.Fragment key={menu.text}>
                <NavItem
                  menu={menu}
                  isOpen={isOpen}
                  isActive={anyChildActive && !isOpen}
                  expanded={drawerOpen}
                  onClick={() => toggleMenu(menu.text)}
                />
                <Collapse in={isOpen} timeout={200} unmountOnExit>
                  <List disablePadding>
                    {menu.children.map((child) => (
                      <ChildItem
                        key={child.text + child.path}
                        child={child}
                        isActive={isSelected(child.path)}
                        expanded={drawerOpen}
                        onClick={() => {
                          navigate(child.path);
                          if (isMobile) setDrawerOpen(false);
                        }}
                      />
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* ── Bottom: User + Upgrade + Logout + Brand ── */}
      <Box sx={{ borderTop: `1px solid ${S.border}`, pt: '8px', pb: '12px' }}>
        {/* Upgrade button (above Logout) */}
        <Tooltip title={drawerOpen ? '' : 'Upgrade Plan'} placement='right' arrow>
          <ListItemButton
            onClick={() => {
              navigate('/admin/Subscription');
              if (isMobile) setDrawerOpen(false);
            }}
            sx={{
              mx: '8px',
              borderRadius: '10px',
              mb: '8px',
              px: drawerOpen ? '12px' : '10px',
              py: '8px',
              justifyContent: drawerOpen ? 'initial' : 'center',
              color: '#86efac',
              background: 'rgba(34,197,94,0.12)',
              border: '1px solid rgba(34,197,94,0.25)',
              transition: 'all 0.15s',
              '&:hover': {
                background: 'rgba(34,197,94,0.2)',
                color: '#bbf7d0',
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? '10px' : 'auto',
                justifyContent: 'center',
                color: 'inherit',
                '& svg': { fontSize: 18 },
              }}>
              <UpgradeIcon />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary='Upgrade Plan'
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: 700,
                  fontFamily: 'Inter,sans-serif',
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* Logout button */}
        <Tooltip title={drawerOpen ? '' : 'Logout'} placement='right' arrow>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: '8px',
              borderRadius: '10px',
              mb: '8px',
              px: drawerOpen ? '12px' : '10px',
              py: '8px',
              justifyContent: drawerOpen ? 'initial' : 'center',
              color: '#f87171',
              transition: 'all 0.15s',
              '&:hover': {
                background: 'rgba(248,113,113,0.1)',
                color: '#fca5a5',
              },
            }}>
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: drawerOpen ? '10px' : 'auto',
                justifyContent: 'center',
                color: 'inherit',
                '& svg': { fontSize: 18 },
              }}>
              <ExitToAppIcon />
            </ListItemIcon>
            {drawerOpen && (
              <ListItemText
                primary='Logout'
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: 'Inter,sans-serif',
                }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        {/* User info */}
        <Box
          sx={{
            mx: '8px',
            px: drawerOpen ? '12px' : '6px',
            py: '8px',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${S.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
          }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              flexShrink: 0,
              background: 'linear-gradient(135deg,#1A56DB,#8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(26,86,219,0.4)',
            }}>
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 800,
                color: '#fff',
                letterSpacing: '0.5px',
              }}>
              {userInitials}
            </Typography>
          </Box>
          {drawerOpen && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'rgba(226,232,240,0.9)',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: 'rgba(100,116,139,0.75)',
                  mt: '1px',
                }}>
                {userRole}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Powered by */}
        {drawerOpen && (
          <Box
            component='a'
            href='https://goerp.live'
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              mt: '10px',
              textDecoration: 'none',
              opacity: 0.45,
              transition: 'opacity 0.2s',
              '&:hover': { opacity: 0.75 },
            }}>
            <Box
              component='img'
              src='/goerp.png'
              alt='GoERP'
              sx={{ height: 16, width: 'auto', objectFit: 'contain' }}
            />
            <Typography
              sx={{
                fontSize: '10px',
                color: 'rgba(148,163,184,0.8)',
                fontWeight: 500,
              }}>
              Powered by Go-ERP.Live
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#F1F5F9', minHeight: '100vh' }}>
      <CssBaseline />

      {/* ── TOP APP BAR ── */}
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 8px rgba(15,23,42,0.06)',
          transition: 'width 0.25s ease, margin 0.25s ease',
          zIndex: theme.zIndex.drawer + 1,
        }}>
        <Toolbar
          sx={{ minHeight: '56px !important', px: { xs: '12px', sm: '20px' } }}>
          <IconButton
            onClick={toggleDrawer}
            sx={{
              mr: '14px',
              color: '#475569',
              width: 36,
              height: 36,
              borderRadius: '10px',
              border: '1px solid rgba(226,232,240,0.8)',
              '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' },
              transition: 'all 0.15s',
            }}>
            {drawerOpen ? (
              <ChevronLeftIcon sx={{ fontSize: 20 }} />
            ) : (
              <MenuIcon sx={{ fontSize: 20 }} />
            )}
          </IconButton>

          {/* #3: Back button on EVERY page (this AppBar wraps all admin pages via
              <Outlet/>). Hidden on the home/landing pages so "Back" never leaves
              the admin area. navigate(-1) = browser-style previous page. */}
          {location.pathname !== '/admin' &&
            location.pathname !== '/admin/dashboard' &&
            location.pathname !== '/admin/resturantdashbord' &&
            location.pathname !== '/admin/welcome' && (
              <Tooltip title='Back' arrow>
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    mr: '12px',
                    color: '#475569',
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    border: '1px solid rgba(226,232,240,0.8)',
                    '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' },
                    transition: 'all 0.15s',
                  }}>
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}

          {/* Title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: '0.85rem', sm: '1rem' },
                fontWeight: 700,
                color: '#0F172A',
                letterSpacing: '-0.2px',
                fontFamily: 'Inter, sans-serif',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {brandName}&nbsp;
              <Box component='span' sx={{ fontWeight: 400, color: '#94a3b8' }}>
                {brandSub}
              </Box>
            </Typography>
          </Box>

          {/* Right side: live badge + user pill */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexShrink: 0,
            }}>
            {/* Live indicator */}
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: '5px',
                bgcolor: alpha('#10b981', 0.1),
                border: `1px solid ${alpha('#10b981', 0.25)}`,
                borderRadius: '20px',
                px: '10px',
                py: '4px',
              }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#10b981',
                  animation: 'apulse 2s infinite',
                  '@keyframes apulse': {
                    '0%,100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.4, transform: 'scale(0.8)' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  color: '#10b981',
                  letterSpacing: '0.3px',
                }}>
                LIVE
              </Typography>
            </Box>

            {/* User pill */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                borderRadius: '10px',
                px: '10px',
                py: '5px',
                cursor: 'default',
              }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: '7px',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg,#1A56DB,#8b5cf6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Typography
                  sx={{ fontSize: '10px', fontWeight: 800, color: '#fff' }}>
                  {userInitials}
                </Typography>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1E293B',
                    lineHeight: 1.1,
                  }}>
                  {userName}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                  {userRole}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── DRAWER ── */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={drawerOpen}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: S.bg,
            border: 'none',
            boxShadow: '4px 0 24px rgba(0,0,0,0.25)',
            transition: 'width 0.25s ease',
            overflowX: 'hidden',
          },
        }}>
        {drawerContent}
      </Drawer>

      {/* ── MAIN CONTENT ── */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          mt: '56px',
          minHeight: 'calc(100vh - 56px)',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          transition: 'width 0.25s ease',
          bgcolor: '#F1F5F9',
          p: 0,
        }}>
        {/* {location.pathname === '/admin' ? (
          hasDashboardPermission ? (
            <Navigate to='/admin/dashboard' replace />
          ) : (
            <Navigate to='/admin/welcome' replace />
          )
        ) : (
          <Outlet />
        )} */}
        {location.pathname === '/admin' ? (
          sessionStorage.getItem('propertType') === 'R' ? (
            <Navigate to='/admin/resturantdashbord' replace />
          ) : hasDashboardPermission ? (
            <Navigate to='/admin/dashboard' replace />
          ) : (
            <Navigate to='/admin/welcome' replace />
          )
        ) : (
          <Outlet />
        )}
      </Box>
    </Box>
  );
}
